// Trades a verified Google token for a signed ACC session token.
//
// Until now the ACC took the operator's identity from `user_name` in the request body, which
// is whatever the browser chose to send - anyone with the API key could act as anyone. The
// session token is signed by the server, so once it is present the server reads identity from
// the token and ignores the body.
//
// Strictly additive on purpose. The server keeps accepting `user_name` until
// `REQUIRE_ACC_SESSION` flips, so every step here is best-effort: no session, no session
// endpoint, or a failed exchange all leave the app behaving exactly as it did before.
//
// Modelled on agentSession.js and lives in its own module for the same reason: it is imported
// by auth.js (eager, via App.js) and uses a bare axios post so it cannot be caught by an
// interceptor that would attach the very session token this is trying to obtain.

import axios from 'axios';
import Cookies from 'js-cookie';
import { ACC_API_URL, ACC_API_KEY } from './constants';
import { getGoogleAccessToken } from './googleAccessToken';
import { getUserName } from './Utils/GeneralUtils';
import { isMockMode } from './Knowledge/mockMode';
import { isKioskSession } from './kioskFlag';

const COOKIE_NAME = 'acc_session';

/**
 * Fired when a session was rejected and there is nothing left to trade for a new one, so the
 * only way back is a fresh Google sign-in. An event rather than a direct call: this module is
 * imported by the command client, which has no business reaching into React state.
 */
export const ACC_SESSION_EXPIRED_EVENT = 'acc-session-expired';

/**
 * A lapsed identity fails every panel at once — the shared iPad logged about
 * ninety 401s in ten minutes on 11 Sep — and each one used to be its own error
 * on its own card. The first signal of a burst is the only one that goes out.
 */
const EXPIRY_QUIET_MS = 30000;
let lastExpiryNoticeMs = 0;

/** True when this call was the one that fired; false when it joined a burst already announced. */
export function notifyAccSessionExpired() {
    const now = Date.now();
    if (lastExpiryNoticeMs && now - lastExpiryNoticeMs < EXPIRY_QUIET_MS) return false;
    lastExpiryNoticeMs = now;
    try {
        window.dispatchEvent(new CustomEvent(ACC_SESSION_EXPIRED_EVENT));
    } catch (e) {
        /* no window to tell */
    }
    return true;
}

/** Signing back in re-arms the takeover for the next lapse. */
export function resetAccSessionExpiry() {
    lastExpiryNoticeMs = 0;
}

// Matches the `user` cookie in auth.js. A cookie rather than the in-memory/sessionStorage spot
// the agent token uses: the server will eventually require this token, so losing it on reload
// would log the whole dashboard out of the backend while the `user` cookie still says signed in.
const SESSION_HOURS = 4;
const SESSION_DAYS = SESSION_HOURS / 24;
const SESSION_MS = SESSION_HOURS * 60 * 60 * 1000;

/** Short: the exchange runs in front of the first data fetch and has a working fallback. */
const EXCHANGE_TIMEOUT_MS = 5000;

// How long to stop retrying after a failed exchange. A failure stores nothing, so without this
// every mount and every refresh re-fires against a backend that is already struggling.
const FAILURE_BACKOFF_MS = 60000;

let inFlight = null;
let inFlightSeq = 0;
let failedUntilMs = 0;

/** The ACC may report expiry as epoch seconds, epoch millis or ISO; normalize to millis. */
export const parseExpiresAt = (value) => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value > 1e12 ? value : value * 1000;
    }
    if (typeof value === 'string') {
        const parsed = Date.parse(value);
        if (!Number.isNaN(parsed)) return parsed;
    }
    // Unreadable expiry: fall back to the cookie's own lifetime. A token the server has already
    // retired 401s, and that clears it.
    return Date.now() + SESSION_MS;
};

const parseEnvelope = (raw) => {
    if (raw && typeof raw === 'object') return raw;
    if (typeof raw !== 'string') return null;
    try {
        return JSON.parse(raw);
    } catch (e) {
        return null;
    }
};

export function getAccSession() {
    let stored;
    try {
        stored = JSON.parse(Cookies.get(COOKIE_NAME) || 'null');
    } catch (e) {
        return null;
    }
    if (typeof stored?.session !== 'string' || stored.session.length === 0) {
        return null;
    }
    if (typeof stored.expiresAt === 'number' && Date.now() >= stored.expiresAt) {
        clearAccSession();
        return null;
    }
    return stored.session;
}

export function clearAccSession() {
    Cookies.remove(COOKIE_NAME);
}

/**
 * Stores a session token. `cookieExpires` is js-cookie's `expires` (days or a Date); the
 * Google path keeps the four-hour default, a PIN session passes its own end time.
 */
export function writeAccSession(session, expiresAt, cookieExpires = SESSION_DAYS) {
    Cookies.set(COOKIE_NAME, JSON.stringify({ session, expiresAt }), {
        expires: cookieExpires,
        sameSite: 'lax',
        // Conditional, not always on: local dev is served over http, where a
        // `secure` cookie is dropped without a word and every request would go
        // out unsigned.
        secure: window.location.protocol === 'https:',
    });
}

async function exchange() {
    // A PIN session has no Google token to trade and must never be replaced by one.
    if (isKioskSession()) {
        return false;
    }
    clearAccSession();
    const googleToken = getGoogleAccessToken();
    // No verifiable identity to trade, so the exchange could only be refused. Mock mode has no
    // backend at all.
    if (!ACC_API_URL || !googleToken || isMockMode()) {
        return false;
    }
    try {
        const response = await axios.post(
            ACC_API_URL,
            { command: 'start_acc_session', user_name: getUserName(), args: [googleToken] },
            { headers: { 'x-api-key': ACC_API_KEY }, timeout: EXCHANGE_TIMEOUT_MS }
        );
        const envelope = parseEnvelope(response.data);
        const session = envelope?.success ? envelope.data?.session : null;
        if (typeof session === 'string' && session.length > 0) {
            writeAccSession(session, parseExpiresAt(envelope.data?.expiresAt));
            return true;
        }
    } catch (e) {
        // Older ACC build, or unreachable. `user_name` still identifies the caller.
    }
    return false;
}

/**
 * Exchanges the current Google token for an ACC session, replacing any existing one.
 *
 * The stored session is dropped first: /login is unguarded, so signing in as a second person
 * without logging out would otherwise leave the first person's token in place and attribute
 * their completions and edits to the wrong operator. That is also why this always starts a
 * fresh exchange rather than joining one already running - it is only ever called when we know
 * the identity behind it just changed.
 *
 * Resolves true only when a token was actually stored. A server without a signing secret
 * answers `session: null`, which is a success on the wire but leaves us on the legacy path.
 *
 * Never consults the failure backoff: this is the login path, where the operator is waiting
 * and a stale minute-long lockout from an earlier tab would leave them unsigned for the
 * backend. It does arm and clear it, so an exchange from either path is accounted for.
 */
export function startAccSession() {
    const seq = ++inFlightSeq;
    // Published so a concurrent ensureAccSession joins this exchange instead of posting its
    // own: login sets the user, which is exactly what makes the mount effect fire.
    const attempt = exchange().then((ok) => {
        failedUntilMs = ok ? 0 : Date.now() + FAILURE_BACKOFF_MS;
        return ok;
    }).finally(() => {
        if (inFlightSeq === seq) {
            inFlight = null;
        }
    });
    inFlight = attempt;
    return attempt;
}

/**
 * Obtains a session if there is not already a usable one. Called on mount as well as at login,
 * because login is not the only way to arrive holding a Google token and no session: anyone
 * already signed in when this ships is in exactly that state.
 */
export async function ensureAccSession() {
    if (getAccSession()) {
        return true;
    }
    if (isKioskSession()) {
        return false;
    }
    // Nothing to trade - not a failure worth backing off from, and the common case after a
    // reload, since the Google token dies with the tab.
    if (!getGoogleAccessToken()) {
        return false;
    }
    // One exchange at a time per tab: several components mount together, and StrictMode runs
    // mount effects twice.
    if (inFlight !== null) {
        return inFlight;
    }
    if (Date.now() < failedUntilMs) {
        return false;
    }
    return startAccSession();
}

/** Test seam: clears the in-flight promise, the failure backoff and the expiry debounce. */
export function resetAccSessionState() {
    inFlight = null;
    inFlightSeq += 1;
    failedUntilMs = 0;
    resetAccSessionExpiry();
}
